import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DataTable from '../components/ResponsiveDataTable';
import { getProductPricing, updateProductPricing, updateWarehouseSettings, getWarehouses, getProductPricingHistory, getPricingChangeHistory, getProductDetail } from '../config/api';
import { toast } from 'react-toastify';

const Products = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [filters, setFilters] = useState({
    category: 'all',
    brand: 'all',
    featured: 'all'
  });
  const [selectedWarehouse, setSelectedWarehouse] = useState(1);
  const [warehouses, setWarehouses] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editedPriceSlabs, setEditedPriceSlabs] = useState([]);
  const [priceChanged, setPriceChanged] = useState(false);
  const [editedActive, setEditedActive] = useState(true);
  const [editedDealOfDay, setEditedDealOfDay] = useState(0);
  const [editedAllowOutOfStock, setEditedAllowOutOfStock] = useState(false);
  const [editedPurchasable, setEditedPurchasable] = useState(true);
  const [editedClearance, setEditedClearance] = useState(false);
  const [editedMoq, setEditedMoq] = useState(1);
  const [saving, setSaving] = useState(false);
  const [userDetails, setUserData] = useState({});
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [pendingSaveData, setPendingSaveData] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [pricingHistory, setPricingHistory] = useState([]);
  const [selectedProductForHistory, setSelectedProductForHistory] = useState(null);
  const [vhModal, setVhModal] = useState(null);
  const [vhSaving, setVhSaving] = useState(false);

  useEffect(() => {
    // Check if user has superAdmin role
    const userData = JSON.parse(localStorage.getItem('userData_30jan') || '{}');
    setUserData(userData);
    if (userData.role !== 'superAdmin' && !userData.access?.includes("marketing")) {
      // Redirect to orders page if not superAdmin and not access of marketing
      window.location.href = '/orders';
      return;
    }
    
    // Load warehouses from API
    loadWarehouses();
  }, []);

  const loadWarehouses = async () => {
    try {
      const response = await getWarehouses();
      if (response && response.data) {
        setWarehouses(response.data);
        // Set default warehouse
        if (response.data.length > 0) {
          setSelectedWarehouse(response.data[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading warehouses:', error);
      toast.error('Failed to load warehouses');
    }
  };

  useEffect(() => {
    if (selectedWarehouse) {
      loadProducts();
    }
  }, [selectedWarehouse]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const data = await getProductPricing(selectedWarehouse) || {};
      console.log(data);
      if(data.data) {
        setProducts(data.data);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (categoryId) => {
    const categories = {
      1: 'Solar Panels',
      2: 'Inverters',
      3: 'BOS',
      4: 'Kit',
      5: 'Battery'
    };
    return categories[categoryId] || 'Unknown';
  };

  const getCategoryBadge = (categoryId) => {
    const badges = {
      1: 'status-badge status-confirmed', // Green for Solar Panels
      2: 'status-badge status-shipped',   // Blue for Inverters
      3: 'status-badge status-pending'    // Yellow for BOS
    };
    return badges[categoryId] || 'status-badge';
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const handleWarehouseChange = (e) => {
    setSelectedWarehouse(parseInt(e.target.value));
    // Cancel any ongoing edits when switching warehouse
    setEditingProduct(null);
    setEditedPriceSlabs([]);
  };

  const getUniqueBrands = () => {
    const brands = [...new Set(products.map(product => product.brand))];
    return brands.sort();
  };

  const handleEdit = (product) => {
    setEditingProduct(product.pid);
    setEditedPriceSlabs([...product.priceSlabs]);
    setPriceChanged(false);
    // Set warehouse-specific fields (using mock data for now)
    setEditedActive(product.active !== undefined ? product.active : true);
    setEditedDealOfDay(product.is_featured || 0);
    setEditedAllowOutOfStock(product.allow_out_of_stock || false);
    setEditedPurchasable(product.purchasable !== undefined ? product.purchasable : true);
    setEditedClearance(product.clearance || false);
    setEditedMoq(product.moq !== undefined && product.moq !== null ? product.moq : 1);
  };

  const handleCancel = () => {
    setEditingProduct(null);
    setEditedPriceSlabs([]);
    setEditedActive(true);
    setEditedDealOfDay(0);
    setEditedAllowOutOfStock(false);
    setEditedPurchasable(true);
    setEditedClearance(false);
    setEditedMoq(1);
  };

  const handlePriceChange = (slabIndex, newPrice) => {
    const updatedSlabs = [...editedPriceSlabs];
    updatedSlabs[slabIndex] = {
      ...updatedSlabs[slabIndex],
      price: parseFloat(newPrice) || 0
    };
    setEditedPriceSlabs(updatedSlabs);
    setPriceChanged(true);
  };

  const handleSlabQuantityChange = (slabIndex, field, value) => {
    const updatedSlabs = [...editedPriceSlabs];
    updatedSlabs[slabIndex] = {
      ...updatedSlabs[slabIndex],
      [field]: value === '' ? '' : parseInt(value) || 0
    };
    setEditedPriceSlabs(updatedSlabs);
    setPriceChanged(true);
  };

  const addPriceSlab = () => {
    const lastSlab = editedPriceSlabs[editedPriceSlabs.length - 1];
    const newQty = lastSlab.max ? lastSlab.max + 1 : (lastSlab.qty + 10);
    
    setEditedPriceSlabs([
      ...editedPriceSlabs,
      { qty: newQty, max: '', price: 0 }
    ]);
    setPriceChanged(true);
  };

  const removePriceSlab = (index) => {
    if (editedPriceSlabs.length > 1) {
      const updatedSlabs = editedPriceSlabs.filter((_, i) => i !== index);
      setEditedPriceSlabs(updatedSlabs);
      setPriceChanged(true);
    }
  };

  const handleSaveClick = (pid) => {
    // Store the data and show warehouse selection modal
    const currentProduct = products.find(p => p.pid === pid);
    setPendingSaveData({
      pid,
      priceSlabs: editedPriceSlabs,
      active: editedActive,
      dealOfDay: editedDealOfDay,
      allow_out_of_stock: editedAllowOutOfStock,
      purchasable: editedPurchasable,
      clearance: editedClearance,
      moq: editedMoq,
      priceChanged,
      inStock: currentProduct?.inStock || 0
    });
    setSelectedWarehouses([selectedWarehouse]); // Pre-select current warehouse
    setShowWarehouseModal(true);
  };

  const handleWarehouseSelection = (warehouseId) => {
    setSelectedWarehouses(prev => {
      if (prev.includes(warehouseId)) {
        return prev.filter(id => id !== warehouseId);
      } else {
        return [...prev, warehouseId];
      }
    });
  };

  const handleConfirmSave = async () => {
    if (selectedWarehouses.length === 0) {
      toast.error('Please select at least one warehouse');
      return;
    }

    setSaving(true);
    try {
      // Call the warehouse settings API to update discount_price, active, is_featured, and allow_out_of_stock
      await updateWarehouseSettings(
        pendingSaveData.pid,
        selectedWarehouses,
        {
          discount_price: pendingSaveData.priceSlabs,
          active: pendingSaveData.active,
          is_featured: pendingSaveData.dealOfDay,
          allow_out_of_stock: pendingSaveData.allow_out_of_stock,
          purchasable: pendingSaveData.purchasable,
          clearance: pendingSaveData.clearance,
          moq: pendingSaveData.moq,
          priceChanged: pendingSaveData.priceChanged,
          inStock: pendingSaveData.inStock
        }
      );
      
      // Update local state
      setProducts(prevProducts => 
        prevProducts.map(product => 
          product.pid === pendingSaveData.pid 
            ? { 
                ...product, 
                priceSlabs: pendingSaveData.priceSlabs,
                active: pendingSaveData.active,
                is_featured: pendingSaveData.dealOfDay,
                allow_out_of_stock: pendingSaveData.allow_out_of_stock,
                purchasable: pendingSaveData.purchasable,
                clearance: pendingSaveData.clearance,
                moq: pendingSaveData.moq
              }
            : product
        )
      );
      
      setEditingProduct(null);
      setEditedPriceSlabs([]);
      setShowWarehouseModal(false);
      setPendingSaveData(null);
      setSelectedWarehouses([]);
      
      const warehouseNames = selectedWarehouses.map(id => 
        warehouses.find(w => w.id === id)?.name
      ).join(', ');
      
      toast.success(`Product settings updated successfully for: ${warehouseNames}`);
    } catch (error) {
      console.error('Error updating product settings:', error);
      toast.error('Failed to update product settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  const filterChangedSnapshots = (snapshots) => {
      if (!snapshots || snapshots.length === 0) return [];

      const changed = [];

      for (let i = 0; i < snapshots.length; i++) {
        const current = snapshots[i];

        if (i === snapshots.length - 1) {
          // Always include the oldest snapshot
          changed.push(current);
        } else {
          const previous = snapshots[i + 1];

          // Check if active status changed
          const statusChanged = current.active !== previous.active;

          // Check if price slabs changed
          const currentSlabs = JSON.stringify(current.discount_price);
          const previousSlabs = JSON.stringify(previous.discount_price);
          const slabsChanged = currentSlabs !== previousSlabs;

          // Only include if something changed
          if (statusChanged || slabsChanged) {
            changed.push(current);
          }
        }
      }

      return changed;
    };

    const handleOpenHistory = async (product) => {
      setSelectedProductForHistory(product);
      setShowHistoryModal(true);
      setHistoryLoading(true);

      try {
        const response = await getPricingChangeHistory(product.pid, selectedWarehouse, 50);
        if (response && response.data) {
          setPricingHistory(response.data);
        } else {
          setPricingHistory([]);
        }
      } catch (error) {
        console.error('Error fetching pricing history:', error);
        toast.error('Failed to load pricing history');
        setPricingHistory([]);
      } finally {
        setHistoryLoading(false);
      }
    };

  const handleCloseHistory = () => {
    setShowHistoryModal(false);
    setSelectedProductForHistory(null);
    setPricingHistory([]);
  };

  const formatPriceRange = (slab, index, slabs) => {
    const startQty = slab.qty;
    const endQty = slab.max || '∞';
    
    if (index === slabs.length - 1 && !slab.max) {
      return `${startQty}+ units`;
    }
    
    return `${startQty}-${endQty} units`;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const columns = [
    {
      name: 'Product Name',
      selector: row => row.productName,
      sortable: true,
      cell: row => (
        <Link 
          to={`/products/${row.pid}`}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <div style={{ fontWeight: '600', color: '#0d6efd' }}>{row.productName}</div>
          <div style={{ fontSize: '12px', color: '#6c757d' }}>{row.skuId}</div>
        </Link>
      ),
      width: '250px'
    },
    {
      name: 'Category',
      selector: row => row.category_id,
      sortable: true,
      cell: row => (
        <span className={getCategoryBadge(row.category_id)}>
          {getCategoryName(row.category_id)}
        </span>
      ),
      width: '150px'
    },
    {
      name: 'Brand',
      selector: row => row.brand,
      sortable: true,
      width: '120px'
    },
    {
      name: 'Active',
      cell: row => (
        editingProduct === row.pid ? (
          <input
            type="checkbox"
            checked={editedActive}
            onChange={(e) => setEditedActive(e.target.checked)}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
        ) : (
          <span className={row.active ? 'status-badge status-confirmed' : 'status-badge status-cancelled'}>
            {row.active ? 'Active' : 'Inactive'}
          </span>
        )
      ),
      width: '100px',
      center: true
    },
    {
      name: 'Deal of Day',
      selector: row => row.is_featured || 0,
      sortable: true,
      cell: row => (
        editingProduct === row.pid ? (
          <input
            type="number"
            value={editedDealOfDay}
            onChange={(e) => setEditedDealOfDay(parseInt(e.target.value) || 0)}
            className="form-control"
            style={{ width: '70px', padding: '4px 8px', fontSize: '12px' }}
            min="0"
            placeholder="0"
            title="0 = not featured, >0 = featured (lower number = higher priority)"
          />
        ) : (
          <span className={row.is_featured > 0 ? 'status-badge status-shipped' : 'status-badge'}>
            {row.is_featured > 0 ? `#${row.is_featured}` : 'No'}
          </span>
        )
      ),
      width: '120px',
      center: true
    },
    {
      name: 'Price Slabs',
      cell: row => (
        <div className="price-slabs-container">
          {(editingProduct === row.pid ? editedPriceSlabs : row.priceSlabs).map((slab, index) => (
            <div key={index} className="price-slab-item" style={{ marginBottom: '8px' }}>
              {editingProduct === row.pid ? (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <input
                      type="number"
                      value={slab.qty}
                      onChange={(e) => handleSlabQuantityChange(index, 'qty', e.target.value)}
                      className="form-control"
                      style={{ width: '60px', padding: '4px 8px', fontSize: '12px' }}
                      min="1"
                      placeholder="From"
                    />
                    <span style={{ fontSize: '12px' }}>to</span>
                    <input
                      type="number"
                      value={slab.max}
                      onChange={(e) => handleSlabQuantityChange(index, 'max', e.target.value)}
                      className="form-control"
                      style={{ width: '60px', padding: '4px 8px', fontSize: '12px' }}
                      min={slab.qty}
                      placeholder="∞"
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <span style={{ fontSize: '12px' }}>₹</span>
                    <input
                      type="number"
                      value={slab.price}
                      onChange={(e) => handlePriceChange(index, e.target.value)}
                      className="form-control price-input"
                      style={{ width: '80px', padding: '4px 8px', fontSize: '12px' }}
                      min="0"
                      step="0.01"
                    />
                  </div>
                  {editedPriceSlabs.length > 1 && (
                    <button
                      onClick={() => removePriceSlab(index)}
                      className="btn btn-secondary"
                      style={{ 
                        fontSize: '10px', 
                        padding: '4px 8px',
                        backgroundColor: '#dc3545',
                        borderColor: '#dc3545'
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="slab-range">
                    {formatPriceRange(slab, index, row.priceSlabs)}
                  </div>
                  <div className="slab-price">
                    <span className="price-display">
                      {formatPrice(slab.price)}
                    </span>
                  </div>
                </>
              )}
            </div>
          ))}
          {editingProduct === row.pid && (
            <button
              onClick={addPriceSlab}
              className="btn btn-primary"
              style={{ fontSize: '11px', padding: '4px 12px', marginTop: '4px' }}
            >
              + Add Slab
            </button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      width: '350px'
    },
    {
      name: 'VH Pricing',
      cell: row => (
        <button
          onClick={async () => {
            try {
              const { getVirtualHubPricing } = await import('../config/api');
              const res = await getVirtualHubPricing(row.pid, selectedWarehouse);
              if (res?.data) setVhModal({ productId: row.pid, productName: row.productName || row.name, ...res.data });
            } catch (e) { toast.error('Failed to load virtual hub pricing'); }
          }}
          className="btn btn-secondary"
          style={{ fontSize: '11px', padding: '5px 10px', background: '#6f42c1', borderColor: '#6f42c1', color: '#fff', whiteSpace: 'nowrap' }}
        >
          VH Price
        </button>
      ),
      width: '100px',
      center: true
    },
    {
      name: 'Per Watt Price',
      cell: row => {
        // Only show for Solar Panels (category_id = 1)
        if (row.category_id !== 1) {
          return <span style={{ color: '#6c757d' }}>N/A</span>;
        }

        const wattage = row.wattage || 0;
        if (wattage === 0) {
          return <span style={{ color: '#6c757d' }}>No wattage</span>;
        }

        const priceSlabs = editingProduct === row.pid ? editedPriceSlabs : row.priceSlabs;
        
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {priceSlabs.map((slab, index) => {
              const perWattPrice = (slab.price / wattage).toFixed(2);
              return (
                <div key={index} style={{ fontSize: '12px' }}>
                  <div style={{ color: '#6c757d', fontSize: '11px' }}>
                    {formatPriceRange(slab, index, priceSlabs)}
                  </div>
                  <div style={{ fontWeight: '600', color: '#0d6efd' }}>
                    ₹{perWattPrice}/W
                  </div>
                </div>
              );
            })}
          </div>
        );
      },
      omit: false,
      width: '150px'
    },
    {
      name: 'In Stock',
      cell: row => {
        const inStock = row.inStock || 0;
        const isInStock = inStock > 0;
        
        return (
          <div style={{ textAlign: 'center' }}>
            <span
              className={isInStock ? 'status-badge status-confirmed' : 'status-badge status-cancelled'}
              style={{
                padding: '6px 12px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '600',
              }}
            >
              {isInStock ? `${inStock} units` : 'Out of Stock'}
            </span>
          </div>
        );
      },
      sortable: true,
      selector: row => row.inStock || 0,
      width: '120px',
      center: true
    },
    {
      name: 'Allow Out of Stock',
      cell: row => (
        editingProduct === row.pid ? (
          <input
            type="checkbox"
            checked={editedAllowOutOfStock}
            onChange={(e) => setEditedAllowOutOfStock(e.target.checked)}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            title="Allow selling this product even when out of stock"
          />
        ) : (
          <span className={row.allow_out_of_stock ? 'status-badge status-confirmed' : 'status-badge status-cancelled'}>
            {row.allow_out_of_stock ? 'Yes' : 'No'}
          </span>
        )
      ),
      width: '140px',
      center: true
    },
    {
      name: 'Purchasable',
      selector: row => row.purchasable ? 1 : 0,
      sortable: true,
      cell: row => (
        editingProduct === row.pid ? (
          <input
            type="checkbox"
            checked={editedPurchasable}
            onChange={(e) => setEditedPurchasable(e.target.checked)}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            title="Whether this product can be purchased"
          />
        ) : (
          <span className={row.purchasable ? 'status-badge status-confirmed' : 'status-badge status-cancelled'}>
            {row.purchasable ? 'Yes' : 'No'}
          </span>
        )
      ),
      width: '120px',
      center: true
    },
    {
      name: 'Clearance',
      selector: row => row.clearance ? 1 : 0,
      sortable: true,
      cell: row => (
        editingProduct === row.pid ? (
          <input
            type="checkbox"
            checked={editedClearance}
            onChange={(e) => setEditedClearance(e.target.checked)}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            title="Mark this product as clearance in this warehouse (shown under the Clearance category in the app)"
          />
        ) : (
          <span className={row.clearance ? 'status-badge status-confirmed' : 'status-badge status-cancelled'}>
            {row.clearance ? 'Yes' : 'No'}
          </span>
        )
      ),
      width: '120px',
      center: true
    },
    {
      name: 'MOQ',
      selector: row => row.moq || 1,
      sortable: true,
      cell: row => (
        editingProduct === row.pid ? (
          <input
            type="number"
            value={editedMoq}
            onChange={(e) => setEditedMoq(parseInt(e.target.value) || 1)}
            className="form-control"
            style={{ width: '70px', padding: '4px 8px', fontSize: '12px' }}
            min="1"
            placeholder="1"
            title="Minimum order quantity"
          />
        ) : (
          <span style={{ fontWeight: '600' }}>{row.moq || 1}</span>
        )
      ),
      width: '80px',
      center: true
    },
    {
      name: 'Actions',
      cell: row => (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {editingProduct === row.pid ? (
            <>
              <button
                onClick={() => handleSaveClick(row.pid)}
                disabled={saving}
                className="btn btn-success"
                style={{ fontSize: '12px', padding: '6px 12px' }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="btn btn-secondary"
                style={{ fontSize: '12px', padding: '6px 12px' }}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              { userDetails && userDetails.access?.includes('pricing') ? <button
                onClick={() => handleEdit(row)}
                className="btn btn-primary"
                style={{ fontSize: '12px', padding: '6px 12px' }}
              >
                Edit
              </button> : "" }
              <button
                onClick={() => handleOpenHistory(row)}
                className="btn btn-info"
                style={{ fontSize: '12px', padding: '6px 12px' }}
                title="View pricing history"
              >
                History
              </button>
              <Link 
                to={`/products/${row.pid}`} 
                className="btn btn-secondary"
                style={{ 
                  fontSize: '12px', 
                  padding: '6px 12px',
                  textDecoration: 'none'
                }}
              >
                View
              </Link>
              <button
                onClick={async () => {
                  try {
                    const res = await getProductDetail(row.pid);
                    const p = res?.data;
                    if (!p) { toast.error('Failed to load product details'); return; }
                    const specs = p.specifications
                      ? Object.entries(p.specifications).map(([key, value]) => ({ key, value }))
                      : [{ key: '', value: '' }];
                    navigate('/products/create', {
                      state: {
                        copyFrom: {
                          category_id: p.category_id || 1,
                          name: p.name || '',
                          description: p.description || '',
                          price: p.price || '',
                          discount_price: p.discount_price || [{ max: '', qty: 1, price: '' }],
                          warranty: p.warranty || '',
                          specifications: specs,
                          brand: p.brand || '',
                          wattage: p.wattage || '',
                          sku_code: '',
                          short_name: p.short_name || '',
                          tags: p.tags || '',
                          dcr_applicable: p.dcr_applicable || false,
                          hsn_code: p.hsn_code || '',
                        }
                      }
                    });
                  } catch (err) {
                    toast.error('Failed to copy product');
                  }
                }}
                className="btn btn-warning"
                style={{ fontSize: '12px', padding: '6px 12px' }}
                title="Copy product"
              >
                Copy
              </button>
            </>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '240px'
    }
  ];

  // Filter data based on search text and filters
  const filteredItems = products.filter(
    item => {
      const searchText = filterText.toLowerCase();
      const matchesSearch = (
        item.productName.toLowerCase().includes(searchText) ||
        item.brand.toLowerCase().includes(searchText) ||
        item.skuId.toLowerCase().includes(searchText) ||
        getCategoryName(item.category_id).toLowerCase().includes(searchText)
      );
      
      const matchesCategory = filters.category === 'all' || item.category_id === parseInt(filters.category);
      const matchesBrand = filters.brand === 'all' || item.brand === filters.brand;
      const matchesFeatured = filters.featured === 'all' || 
        (filters.featured === 'featured' && item.is_featured > 0) ||
        (filters.featured === 'not_featured' && (item.is_featured === 0 || !item.is_featured)) ||
        (filters.featured === 'in_stock' && (item.inStock > 0));
      
      return matchesSearch && matchesCategory && matchesBrand && matchesFeatured;
    }
  );

  const subHeaderComponent = (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      width: '100%',
      padding: '8px 0',
      gap: '15px',
      flexWrap: 'wrap'
    }}>
      <div style={{ position: 'relative', minWidth: '250px' }}>
        <input
          type="text"
          placeholder="Search products..."
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          style={{
            padding: '10px 14px',
            border: '2px solid #e2e8f0',
            borderRadius: '6px',
            fontSize: '14px',
            width: '100%',
            transition: 'all 0.3s ease',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#0d6efd';
            e.target.style.boxShadow = '0 0 0 2px rgba(13, 110, 253, 0.25)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#ced4da';
            e.target.style.boxShadow = 'none';
          }}
        />
      </div>
      
      <div style={{ minWidth: '150px' }}>
        <select
          name="category"
          value={filters.category}
          onChange={handleFilterChange}
          style={{
            padding: '10px 14px',
            border: '2px solid #e2e8f0',
            borderRadius: '6px',
            fontSize: '14px',
            width: '100%',
            backgroundColor: 'white'
          }}
        >
          <option value="all">All Categories</option>
          <option value="1">Solar Panels</option>
          <option value="2">Inverters</option>
          <option value="3">BOS</option>
          <option value="4">Kit</option>
          <option value="5">Battery</option>
        </select>
      </div>
      
      <div style={{ minWidth: '150px' }}>
        <select
          name="brand"
          value={filters.brand}
          onChange={handleFilterChange}
          style={{
            padding: '10px 14px',
            border: '2px solid #e2e8f0',
            borderRadius: '6px',
            fontSize: '14px',
            width: '100%',
            backgroundColor: 'white'
          }}
        >
          <option value="all">All Brands</option>
          {getUniqueBrands().map(brand => (
            <option key={brand} value={brand}>{brand}</option>
          ))}
        </select>
      </div>
      
      <div style={{ minWidth: '150px' }}>
        <select
          name="featured"
          value={filters.featured}
          onChange={handleFilterChange}
          style={{
            padding: '10px 14px',
            border: '2px solid #e2e8f0',
            borderRadius: '6px',
            fontSize: '14px',
            width: '100%',
            backgroundColor: 'white'
          }}
        >
          <option value="all">All Products</option>
          <option value="featured">Featured Only</option>
          <option value="not_featured">Not Featured</option>
          <option value="in_stock">InStock Only</option>
        </select>
      </div>
      
      {(filters.category !== 'all' || filters.brand !== 'all' || filters.featured !== 'all' || filterText) && (
        <button
          onClick={() => {
            setFilters({ category: 'all', brand: 'all', featured: 'all' });
            setFilterText('');
          }}
          style={{
            padding: '10px 16px',
            border: '1px solid #6c757d',
            borderRadius: '6px',
            fontSize: '14px',
            backgroundColor: '#6c757d',
            color: 'white',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#5c636a';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = '#6c757d';
          }}
        >
          Clear Filters
        </button>
      )}
    </div>
  );

  const customStyles = {
    header: {
      style: {
        minHeight: '56px',
        paddingLeft: '0px',
        paddingRight: '0px',
      },
    },
    headRow: {
      style: {
        borderTopStyle: 'solid',
        borderTopWidth: '1px',
        borderTopColor: '#dee2e6',
        backgroundColor: '#f8f9fa',
      },
    },
    headCells: {
      style: {
        '&:not(:last-of-type)': {
          borderRightStyle: 'solid',
          borderRightWidth: '1px',
          borderRightColor: '#dee2e6',
        },
        backgroundColor: '#f8f9fa',
        fontWeight: '600',
        fontSize: '14px',
        color: '#495057',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        padding: '16px',
      },
    },
    cells: {
      style: {
        '&:not(:last-of-type)': {
          borderRightStyle: 'solid',
          borderRightWidth: '1px',
          borderRightColor: '#dee2e6',
        },
        padding: '16px',
        fontSize: '14px',
      },
    },
    rows: {
      style: {
        '&:hover': {
          backgroundColor: '#f8f9fa',
          cursor: 'pointer',
        },
        borderBottomColor: '#dee2e6',
      },
    },
    pagination: {
      style: {
        borderTop: '1px solid #dee2e6',
        padding: '16px',
      },
    },
  };

  const conditionalRowStyles = [
    {
      when: row => row.active === true,
      style: {
        backgroundColor: '#d4edda',
        '&:hover': {
          backgroundColor: '#c3e6cb',
        },
      },
    },
    {
      when: row => row.active === false,
      style: {
        backgroundColor: '#f8d7da',
        '&:hover': {
          backgroundColor: '#f5c6cb',
        },
      },
    },
  ];

  return (
    <div className="container">
      {/* Page Header */}
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 className="page-title">Product Management</h1>
            <p className="page-subtitle">Manage your product catalog</p>
          </div>
          <Link
            to="/products/create"
            className="btn btn-success"
            style={{ fontSize: '14px', padding: '12px 20px' }}
          >
            + Add Product
          </Link>
        </div>
      </div>

      {/* Warehouse Selector */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ padding: '20px' }}>
          <label style={{ fontWeight: '600', marginBottom: '10px', display: 'block' }}>
            Select Warehouse
          </label>
          <select
            value={selectedWarehouse || ''}
            onChange={handleWarehouseChange}
            style={{
              padding: '10px 14px',
              border: '2px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '14px',
              width: '100%',
              maxWidth: '300px',
              backgroundColor: 'white'
            }}
          >
            {warehouses.map(warehouse => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name} - {warehouse.state}
              </option>
            ))}
          </select>
          <p style={{ fontSize: '12px', color: '#6c757d', marginTop: '8px' }}>
            Products shown are specific to the selected warehouse
          </p>
        </div>
      </div>

      {/* Data Table Card */}
      <div className="card">
        <DataTable
          columns={columns}
          data={filteredItems}
          progressPending={loading}
          pagination
          paginationResetDefaultPage={false}
          subHeader
          subHeaderComponent={subHeaderComponent}
          selectableRows={false}
          customStyles={customStyles}
          conditionalRowStyles={conditionalRowStyles}
          highlightOnHover
          pointerOnHover
          responsive
          striped
          forceDesktop
          paginationPerPage={10}
          paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
          noDataComponent={
            <div style={{ 
              padding: '60px 40px', 
              textAlign: 'center',
              color: '#6c757d',
              fontSize: '16px'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '8px' }}>No products found</div>
              <div>Try adjusting your search criteria, filters, or add a new product</div>
            </div>
          }
          defaultSortFieldId="productName"
          defaultSortAsc={true}
        />
      </div>

      {/* Warehouse Selection Modal */}
      {showWarehouseModal && (
        <div className="modal-overlay" onClick={() => !saving && setShowWarehouseModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Apply Changes to Warehouses</h3>
              <button 
                className="modal-close"
                onClick={() => !saving && setShowWarehouseModal(false)}
                disabled={saving}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <p style={{ marginBottom: '20px', color: '#6c757d' }}>
                Select the warehouses where you want to apply these pricing and settings changes:
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {warehouses.map(warehouse => (
                  <label 
                    key={warehouse.id}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      padding: '12px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      backgroundColor: selectedWarehouses.includes(warehouse.id) ? '#e7f3ff' : 'white',
                      borderColor: selectedWarehouses.includes(warehouse.id) ? '#0d6efd' : '#e2e8f0'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedWarehouses.includes(warehouse.id)}
                      onChange={() => handleWarehouseSelection(warehouse.id)}
                      style={{ 
                        width: '18px', 
                        height: '18px', 
                        marginRight: '12px',
                        cursor: 'pointer'
                      }}
                    />
                    <div>
                      <div style={{ fontWeight: '600' }}>{warehouse.name}</div>
                      <div style={{ fontSize: '12px', color: '#6c757d' }}>{warehouse.location}</div>
                    </div>
                  </label>
                ))}
              </div>
              
              {selectedWarehouses.length === 0 && (
                <p style={{ color: '#dc3545', fontSize: '14px', marginTop: '12px' }}>
                  Please select at least one warehouse
                </p>
              )}
            </div>
            
            <div className="modal-footer">
              <button
                onClick={() => !saving && setShowWarehouseModal(false)}
                className="btn btn-secondary"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSave}
                className="btn btn-success"
                disabled={saving || selectedWarehouses.length === 0}
              >
                {saving ? 'Applying Changes...' : `Apply to ${selectedWarehouses.length} Warehouse${selectedWarehouses.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pricing History Modal */}
      {showHistoryModal && selectedProductForHistory && (
        <div className="modal-overlay" onClick={() => !historyLoading && handleCloseHistory()}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '80vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <div>
                <h3>Pricing History</h3>
                <p style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                  {selectedProductForHistory.productName} ({selectedProductForHistory.skuId})
                </p>
              </div>
              <button 
                className="modal-close"
                onClick={handleCloseHistory}
                disabled={historyLoading}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              {historyLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{ fontSize: '14px', color: '#6c757d' }}>Loading pricing history...</div>
                </div>
              ) : pricingHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{ fontSize: '14px', color: '#6c757d' }}>No pricing history found</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {pricingHistory.map((history, index) => (
                    <div 
                      key={history.id} 
                      style={{ 
                        border: '1px solid #e2e8f0', 
                        borderRadius: '6px', 
                        padding: '12px',
                        backgroundColor: '#f8f9fa'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '14px' }}>
                            Changed by: {history.changed_by_email || 'Unknown'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                            {new Date(history.createdAt).toLocaleString('en-IN', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit'
                            })}
                          </div>
                        </div>
                      </div>
                      
                      <div style={{ marginTop: '12px' }}>
                        <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '8px', color: '#495057' }}>
                          Changes:
                        </div>
                        <div style={{ fontSize: '12px', color: '#374151', marginBottom: '8px' }}>
                          {history.change_description || 'No changes recorded'}
                        </div>
                      </div>

                      <div style={{ marginTop: '12px' }}>
                        <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '8px', color: '#495057' }}>
                          New Price Slabs:
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {history.new_price_slabs && Array.isArray(history.new_price_slabs) ? (
                            history.new_price_slabs.map((slab, slabIndex) => (
                              <div 
                                key={slabIndex}
                                style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between',
                                  padding: '8px',
                                  backgroundColor: 'white',
                                  borderRadius: '4px',
                                  fontSize: '12px'
                                }}
                              >
                                <span>
                                  {slab.qty} {slab.max ? `- ${slab.max}` : '+'} units
                                </span>
                                <span style={{ fontWeight: '600', color: '#0d6efd' }}>
                                  ₹{slab.price.toFixed(2)}
                                </span>
                              </div>
                            ))
                          ) : (
                            <div style={{ fontSize: '12px', color: '#6c757d' }}>No price data available</div>
                          )}
                        </div>
                      </div>

                      {history.old_price_slabs && (
                        <div style={{ marginTop: '12px' }}>
                          <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '8px', color: '#6c757d' }}>
                            Previous Price Slabs:
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {Array.isArray(history.old_price_slabs) ? (
                              history.old_price_slabs.map((slab, slabIndex) => (
                                <div 
                                  key={slabIndex}
                                  style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between',
                                    padding: '8px',
                                    backgroundColor: 'white',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    opacity: 0.7
                                  }}
                                >
                                  <span>
                                    {slab.qty} {slab.max ? `- ${slab.max}` : '+'} units
                                  </span>
                                  <span style={{ fontWeight: '600', color: '#6c757d' }}>
                                    ₹{slab.price.toFixed(2)}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <div style={{ fontSize: '12px', color: '#6c757d' }}>No previous price data</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button
                onClick={handleCloseHistory}
                className="btn btn-secondary"
                disabled={historyLoading}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Virtual Hub Pricing Modal */}
      {vhModal && (
        <div onClick={() => setVhModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '10px', width: '90%', maxWidth: '700px', maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '16px' }}>Virtual Hub Pricing — {vhModal.productName}</h3>
              <button onClick={() => setVhModal(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <p style={{ fontSize: '12px', color: '#6c757d', marginBottom: '16px' }}>
                Parent slabs: {(vhModal.parentSlabs || []).map(s => `${s.qty}+ → ₹${s.price}`).join(' | ')}
              </p>
              {(vhModal.virtualHubs || []).length === 0 ? (
                <p style={{ color: '#6c757d', textAlign: 'center' }}>No virtual hubs found for this parent.</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Virtual Hub</th>
                      <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', width: '100px' }}>Variation %</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Computed Slabs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vhModal.virtualHubs.map(vh => (
                      <tr key={vh.hubId}>
                        <td style={{ padding: '8px', fontWeight: '500', borderBottom: '1px solid #f0f0f0' }}>{vh.hubName}</td>
                        <td style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>
                          <input type="number" step="0.5" min="-50" max="100"
                            style={{ width: '70px', fontSize: '13px', textAlign: 'center', padding: '4px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                            value={vh.priceVariationPercent}
                            onChange={e => {
                              const newPct = parseFloat(e.target.value) || 0;
                              setVhModal(prev => ({
                                ...prev,
                                virtualHubs: prev.virtualHubs.map(v => v.hubId === vh.hubId ? {
                                  ...v,
                                  priceVariationPercent: newPct,
                                  slabs: (prev.parentSlabs || []).map(s => ({ ...s, price: parseFloat((s.price * (1 + newPct / 100)).toFixed(2)) }))
                                } : v)
                              }));
                            }}
                          />
                        </td>
                        <td style={{ padding: '8px', fontSize: '12px', color: '#374151', borderBottom: '1px solid #f0f0f0' }}>
                          {(vh.slabs || []).map(s => `${s.qty}+ → ₹${s.price}`).join(' | ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setVhModal(null)} className="btn btn-secondary" style={{ fontSize: '13px' }}>Cancel</button>
              <button disabled={vhSaving} className="btn btn-primary" style={{ fontSize: '13px' }} onClick={async () => {
                setVhSaving(true);
                try {
                  const { updateVirtualHubPricing } = await import('../config/api');
                  await updateVirtualHubPricing(vhModal.productId, selectedWarehouse, vhModal.virtualHubs.map(v => ({ hubId: v.hubId, priceVariationPercent: v.priceVariationPercent })));
                  toast.success('Virtual hub pricing updated');
                  setVhModal(null);
                } catch (e) { toast.error(e.message || 'Failed to update'); }
                finally { setVhSaving(false); }
              }}>
                {vhSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;